﻿import { useState, useEffect } from 'react';
import { useAuthStore } from '../hooks/useAuthStore';
import { useNavigate } from 'react-router-dom';
import { Stethoscope, Calendar, Clock, CheckCircle, ArrowLeft, User, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';

const API = import.meta.env.VITE_API_URL || '/api';

const TIME_SLOTS = ['09:00','09:15','09:30','09:45','10:00','10:15','10:30','10:45','11:00','11:15','11:30','11:45','13:00','13:15','13:30','13:45','14:00','14:15','14:30','14:45','15:00','15:15','15:30','15:45','16:00','16:15','16:30','16:45'];

export default function BookAppointment() {
  const { user, token } = useAuthStore();
  const navigate = useNavigate();
  const headers = { Authorization: `Bearer ${token}` };

  const [step, setStep] = useState(1); // 1=select doctor, 2=select time, 3=confirm, 4=done
  const [doctors, setDoctors] = useState([]);
  const [loadingDoctors, setLoadingDoctors] = useState(true);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [complaint, setComplaint] = useState('');
  const [booking, setBooking] = useState(false);
  const [bookedAppointment, setBookedAppointment] = useState(null);
  const [profileWarning, setProfileWarning] = useState(false);

  useEffect(() => {
    loadDoctors();
    checkPatientProfile();
  }, []);

  const checkPatientProfile = async () => {
    try {
      const res = await fetch(`${API}/patient/profile.php`, { headers }).then(r => r.json());
      if (res.success && !res.profile?.profile_complete) {
        setProfileWarning(true);
      }
    } catch {}
  };

  const loadDoctors = async () => {
    setLoadingDoctors(true);
    try {
      const res = await fetch(`${API}/doctors/list.php?specialty=GP`, { headers }).then(r => r.json());
      if (res.success) setDoctors(res.doctors);
    } catch (err) {
      console.error('Failed to load doctors:', err);
    } finally {
      setLoadingDoctors(false);
    }
  };

  // Get next 14 days (excluding weekends if doctor unavailable)
  const getAvailableDates = () => {
    if (!selectedDoctor) return [];
    const availDays = (selectedDoctor.available_days || 'Mon,Tue,Wed,Thu,Fri').split(',');
    const dayMap = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
    const dates = [];
    const today = new Date();
    for (let i = 1; i <= 21; i++) {
      const d = new Date(today);
      d.setDate(today.getDate() + i);
      const dayName = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][d.getDay()];
      if (availDays.includes(dayName)) {
        dates.push({
          date: d.toISOString().split('T')[0],
          label: d.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' })
        });
        if (dates.length >= 10) break;
      }
    }
    return dates;
  };

  const confirmBooking = async () => {
    if (!selectedDoctor || !selectedDate || !selectedTime) return;
    setBooking(true);
    try {
      const scheduledAt = `${selectedDate} ${selectedTime}:00`;
      const res = await fetch(`${API}/appointments/index.php`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          doctor_user_id: selectedDoctor.id,
          scheduled_at: scheduledAt,
          chief_complaint: complaint,
          appointment_type: 'gp',
          duration_minutes: 15
        }),
      }).then(r => r.json());
      
      if (res.success) {
        setBookedAppointment(res);
        setStep(4);
        toast.success('Appointment booked!');
      }
    } catch (err) {
      toast.error('Failed to book appointment');
    } finally {
      setBooking(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 to-purple-50">
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="flex items-center space-x-3 mb-6">
          <Link to="/dashboard" className="text-gray-500 hover:text-gray-700">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Book a Video Consultation</h1>
            <p className="text-sm text-gray-500 mt-0.5">See a GP via secure video call</p>
          </div>
        </div>

        {/* Profile warning */}
        {profileWarning && (
          <div className="mb-4 bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start space-x-3">
            <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-amber-800">Complete your health profile first</p>
              <p className="text-xs text-amber-700 mt-1">Your doctor needs your medical history to provide the best care.</p>
              <Link to="/patient-profile" className="text-xs text-amber-800 underline font-medium mt-1 block">Complete profile →</Link>
            </div>
          </div>
        )}

        {/* Step indicator */}
        {step < 4 && (
          <div className="flex items-center mb-8">
            {['Select Doctor','Choose Time','Confirm'].map((label, i) => (
              <div key={i} className="flex items-center flex-1">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${step > i + 1 ? 'bg-green-500 text-white' : step === i + 1 ? 'bg-purple-700 text-white' : 'bg-gray-200 text-gray-500'}`}>
                  {step > i + 1 ? '✓' : i + 1}
                </div>
                <span className={`ml-2 text-sm hidden sm:block ${step === i + 1 ? 'text-blue-700 font-medium' : 'text-gray-400'}`}>{label}</span>
                {i < 2 && <div className="flex-1 h-0.5 bg-gray-200 mx-3"></div>}
              </div>
            ))}
          </div>
        )}

        <div className="bg-white rounded-xl shadow-lg p-6">
          {/* STEP 1: Select Doctor */}
          {step === 1 && (
            <div>
              <h2 className="text-lg font-bold text-gray-900 mb-1">Available GP Doctors</h2>
              <p className="text-sm text-gray-500 mb-5">All patients must first see a GP. Your GP can then refer you to a specialist.</p>
              
              {loadingDoctors ? (
                <div className="text-center py-8">
                  <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
                  <p className="text-sm text-gray-500 mt-3">Loading available doctors...</p>
                </div>
              ) : doctors.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  <Stethoscope className="w-16 h-16 mx-auto mb-4 opacity-20" />
                  <p className="font-medium">No GPs available for booking</p>
                  <p className="text-sm mt-2">Please check back later or contact our clinic directly</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {doctors.map(doctor => (
                    <div key={doctor.id}
                      onClick={() => { setSelectedDoctor(doctor); setStep(2); }}
                      className="flex items-start space-x-4 p-5 border-2 border-gray-200 rounded-xl hover:border-blue-400 cursor-pointer transition-all hover:bg-purple-50 group">
                      <div className="w-14 h-14 bg-purple-100 rounded-full flex items-center justify-center shrink-0">
                        <span className="text-2xl">👨‍⚕️</span>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <h3 className="font-bold text-gray-900 text-lg">{doctor.full_name}</h3>
                          <div className="text-right">
                            <p className="text-sm font-semibold text-purple-700">
                              {doctor.consultation_fee > 0 ? `$${doctor.consultation_fee}` : 'Bulk Billed'}
                            </p>
                            <p className="text-xs text-gray-400">15 min</p>
                          </div>
                        </div>
                        <p className="text-purple-700 text-sm font-medium">General Practitioner</p>
                        {doctor.qualifications && <p className="text-xs text-gray-500 mt-1">{doctor.qualifications}</p>}
                        {doctor.bio && <p className="text-sm text-gray-600 mt-2">{doctor.bio}</p>}
                        {doctor.clinic_name && (
                          <p className="text-xs text-gray-400 mt-2">📍 {doctor.clinic_name}{doctor.clinic_suburb ? `, ${doctor.clinic_suburb}` : ''}</p>
                        )}
                        <p className="text-xs text-gray-400 mt-1">⏰ {doctor.available_days} · {doctor.available_from?.slice(0,5)} – {doctor.available_to?.slice(0,5)}</p>
                      </div>
                      <div className="text-blue-400 group-hover:text-purple-700 transition-colors">→</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* STEP 2: Select Date & Time */}
          {step === 2 && selectedDoctor && (
            <div>
              <button onClick={() => setStep(1)} className="flex items-center space-x-1 text-sm text-gray-500 hover:text-gray-700 mb-4">
                <ArrowLeft className="w-4 h-4" /> <span>Back</span>
              </button>
              <div className="flex items-center space-x-3 mb-5 p-4 bg-purple-50 rounded-lg">
                <span className="text-3xl">👨‍⚕️</span>
                <div>
                  <p className="font-bold text-gray-900">{selectedDoctor.full_name}</p>
                  <p className="text-sm text-purple-700">General Practitioner · Video Consultation</p>
                </div>
              </div>

              <h3 className="font-semibold text-gray-900 mb-3">Select a date</h3>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-5">
                {getAvailableDates().map(d => (
                  <button key={d.date} onClick={() => { setSelectedDate(d.date); setSelectedTime(''); }}
                    className={`py-2 px-3 text-xs rounded-lg border-2 transition-colors ${selectedDate === d.date ? 'border-blue-600 bg-purple-700 text-white' : 'border-gray-200 hover:border-blue-300 text-gray-700'}`}>
                    {d.label}
                  </button>
                ))}
              </div>

              {selectedDate && (
                <>
                  <h3 className="font-semibold text-gray-900 mb-3">Select a time</h3>
                  <div className="grid grid-cols-4 sm:grid-cols-7 gap-2 mb-5">
                    {TIME_SLOTS.map(t => (
                      <button key={t} onClick={() => setSelectedTime(t)}
                        className={`py-2 text-xs rounded-lg border-2 transition-colors ${selectedTime === t ? 'border-blue-600 bg-purple-700 text-white' : 'border-gray-200 hover:border-blue-300 text-gray-700'}`}>
                        {t}
                      </button>
                    ))}
                  </div>
                </>
              )}

              <div className="mb-5">
                <label className="block text-sm font-medium text-gray-700 mb-2">What is your main concern? (optional)</label>
                <textarea value={complaint} onChange={e => setComplaint(e.target.value)} rows={3}
                  placeholder="Brief description of your symptoms or reason for consultation..."
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500" />
              </div>

              <div className="bg-purple-50 rounded-lg p-4 mb-5 text-sm text-purple-800">
                <p>⚠️ <strong>Note:</strong> Please complete your AI consultation (Instant Dr Chat) before your appointment so your doctor can review your case notes.</p>
              </div>

              <button onClick={() => setStep(3)} disabled={!selectedDate || !selectedTime}
                className="w-full bg-purple-700 hover:bg-purple-800 disabled:opacity-50 text-white font-bold py-3 px-6 rounded-xl transition-colors">
                Continue →
              </button>
            </div>
          )}

          {/* STEP 3: Confirm */}
          {step === 3 && (
            <div>
              <button onClick={() => setStep(2)} className="flex items-center space-x-1 text-sm text-gray-500 hover:text-gray-700 mb-4">
                <ArrowLeft className="w-4 h-4" /> <span>Back</span>
              </button>
              <h2 className="text-lg font-bold text-gray-900 mb-5">Confirm Appointment</h2>
              
              <div className="space-y-4 mb-6">
                <div className="flex justify-between p-4 bg-gray-50 rounded-lg">
                  <span className="text-gray-600">Doctor</span>
                  <span className="font-semibold text-gray-900">{selectedDoctor?.full_name}</span>
                </div>
                <div className="flex justify-between p-4 bg-gray-50 rounded-lg">
                  <span className="text-gray-600">Date & Time</span>
                  <span className="font-semibold text-gray-900">
                    {new Date(`${selectedDate} ${selectedTime}`).toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long' })} at {selectedTime}
                  </span>
                </div>
                <div className="flex justify-between p-4 bg-gray-50 rounded-lg">
                  <span className="text-gray-600">Type</span>
                  <span className="font-semibold text-gray-900">Video Consultation (15 min)</span>
                </div>
                <div className="flex justify-between p-4 bg-gray-50 rounded-lg">
                  <span className="text-gray-600">Fee</span>
                  <span className="font-semibold text-gray-900">{selectedDoctor?.consultation_fee > 0 ? `$${selectedDoctor.consultation_fee}` : '🩺 Bulk Billed'}</span>
                </div>
                {complaint && (
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-gray-600 mb-1">Chief complaint</p>
                    <p className="text-gray-900">{complaint}</p>
                  </div>
                )}
              </div>
              
              <p className="text-xs text-gray-500 mb-5">By confirming, you agree that a video consultation link will be generated. The doctor will join at the scheduled time.</p>
              
              <button onClick={confirmBooking} disabled={booking}
                className="w-full bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white font-bold py-4 px-6 rounded-xl transition-colors text-lg">
                {booking ? 'Booking...' : '✅ Confirm Booking'}
              </button>
            </div>
          )}

          {/* STEP 4: Booked */}
          {step === 4 && bookedAppointment && (
            <div className="text-center py-6">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Appointment Booked! 🎉</h2>
              <p className="text-gray-600 mb-6">Your video consultation has been scheduled with {selectedDoctor?.full_name}</p>
              
              <div className="bg-purple-50 rounded-xl p-5 mb-6 text-left space-y-2">
                <p className="text-sm text-gray-700">📅 <strong>Date:</strong> {new Date(`${selectedDate} ${selectedTime}`).toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long' })} at {selectedTime}</p>
                <p className="text-sm text-gray-700">👨‍⚕️ <strong>Doctor:</strong> {selectedDoctor?.full_name}</p>
                <p className="text-sm text-gray-700">📹 <strong>Room:</strong> <span className="font-mono text-xs">{bookedAppointment.video_room}</span></p>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Link to={`/video-chat?room=${bookedAppointment.video_room}`}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-3 px-5 rounded-xl text-center transition-colors">
                  📹 Go to Video Room
                </Link>
                <Link to="/my-appointments" className="bg-purple-700 hover:bg-purple-800 text-white font-bold py-3 px-5 rounded-xl text-center transition-colors">
                  📅 View Appointments
                </Link>
              </div>
              <div className="mt-4">
                <Link to="/my-consult-openai" className="text-sm text-purple-700 hover:underline">
                  💬 Do Instant Dr Chat before your appointment →
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
